/**
 * AI Manager API를 정의
 *
 * @author Max <sud665@ymjlab.com>
 * @version 1.0.0
 * @since 2025-01-20
 */
import { ENV } from "@/constants/env";
import instance, { ApiResponse } from "./instance";

// 🔥 prefix 포함하여 올바른 경로 설정
const PREFIX_PATH = `${ENV.PRE_FIX}/${ENV.ADMIN_PATH}/ai-manager/super-admin`;

// === 인터페이스 정의 ===

export interface Tool {
  toolId: number;
  toolName: string;
  description: string;
  category: string;
  isActive: boolean;
  timeoutSeconds: number;
  executionCount: number;
  successRate: number;
  avgExecutionTime: number;
  lastExecutedAt: string;
  comId?: number; // 도장별 조회시 추가
}

export interface Task {
  taskId: number;
  taskName: string;
  description: string;
  category: string;
  isActive: boolean;
  tools: string[];
  executionCount: number;
  successRate: number;
  lastExecutedAt: string;
  comId?: number; // 도장별 조회시 추가
}

export interface Statistics {
  totalTools: number;
  activeTools: number;
  totalTasks: number;
  activeTasks: number;
  todayExecutions: number;
  avgSuccessRate: number;
  comId?: number; // 도장별 조회시 추가
}

export interface Company {
  comId: number;
  comNm: string;
}

export interface ToolDetail {
  toolId: number;
  toolName: string;
  description: string;
  category: string;
  isActive: boolean;
  timeoutSeconds: number;
  executionCount: number;
  successRate: number;
  avgExecutionTime: number;
  lastExecutedAt: string;
  schema: {
    properties: Record<string, any>;
    required: string[];
    type: string;
  };
  dependencies: Array<{
    name: string;
    version: string;
    type: 'external' | 'internal';
  }>;
  executionHistory: Array<{
    id: number;
    executedAt: string;
    status: string;
    duration: number;
    result?: any;
    error?: string;
  }>;
}

export interface TaskDetail {
  taskId: number;
  taskName: string;
  description: string;
  isActive: boolean;
  executionCount: number;
  successRate: number;
  avgExecutionTime: number;
  lastExecutedAt: string;
  config: {
    trigger: string;
    priority: string;
    timeout: number;
    retryCount: number;
    parameters: Record<string, any>;
  };
  executionHistory: Array<{
    id: number;
    executedAt: string;
    status: string;
    duration: number;
    result?: any;
    error?: string;
  }>;
}

export interface ToolStatusUpdateRequest {
  isActive: boolean;
}

export interface TaskStatusUpdateRequest {
  isActive: boolean;
}

export interface CompanyUsageRequest {
  comId: number;
  isEnabled: boolean;
}

// === API 함수 정의 ===

/**
 * 도장 목록 조회
 * @returns {Promise<Company[]>}
 */
export const getCompanies = async (): Promise<Company[]> => {
  try {
    // 전체 도장 목록을 가져오기 위해 limit을 충분히 크게 설정
    const response = await instance.get(`${ENV.PRE_FIX}/${ENV.ADMIN_PATH}/company-stamps?limit=1000`) as any;
    
    console.log('🔍 Company API Response:', response);
    
    // ListResponseDto 구조 확인 (list, count, page, limit 필드)
    if (response && response.list && Array.isArray(response.list)) {
      console.log('✅ Found companies in list field:', response.list.length);
      return response.list.map((item: any) => ({
        comId: parseInt(item.comId), // 문자열을 숫자로 변환
        comNm: item.comNm || item.name // comNm 또는 name 필드 모두 지원
      }));
    }
    
    // 표준 ResponseDataDto 구조인 경우
    if (response && response.success && response.data) {
      if (response.data.list && Array.isArray(response.data.list)) {
        console.log('✅ Found companies in data.list field:', response.data.list.length);
        return response.data.list.map((item: any) => ({
          comId: parseInt(item.comId),
          comNm: item.comNm || item.name
        }));
      }
      if (Array.isArray(response.data)) {
        console.log('✅ Found companies in data field:', response.data.length);
        return response.data.map((item: any) => ({
          comId: parseInt(item.comId),
          comNm: item.comNm || item.name
        }));
      }
    }
    
    // 직접 배열인 경우
    if (Array.isArray(response)) {
      console.log('✅ Found companies as direct array:', response.length);
      return response.map((item: any) => ({
        comId: parseInt(item.comId),
        comNm: item.comNm || item.name
      }));
    }
    
    console.warn('⚠️ Unexpected company API response structure:', response);
    return [];
  } catch (error) {
    console.error('❌ Failed to load companies:', error);
    return [];
  }
};

/**
 * Tools 목록 조회
 * @param comId 도장 ID (선택적)
 * @returns {Promise<Tool[]>}
 */
export const getTools = async (comId?: number): Promise<Tool[]> => {
  const queryParam = comId ? `?comId=${comId}` : '';
  const response = await instance.get(
    `${PREFIX_PATH}/tools${queryParam}`
  ) as ApiResponse<Tool[]>;
  
  if (response.success) {
    return response.data || [];
  }
  return [];
};

/**
 * Tasks 목록 조회
 * @param comId 도장 ID (선택적)
 * @returns {Promise<Task[]>}
 */
export const getTasks = async (comId?: number): Promise<Task[]> => {
  const queryParam = comId ? `?comId=${comId}` : '';
  const response = await instance.get(
    `${PREFIX_PATH}/tasks${queryParam}`
  ) as ApiResponse<Task[]>;
  
  if (response.success) {
    return response.data || [];
  }
  return [];
};

/**
 * 통계 정보 조회
 * @param comId 도장 ID (선택적)
 * @returns {Promise<Statistics>}
 */
export const getStatistics = async (comId?: number): Promise<Statistics> => {
  const queryParam = comId ? `?comId=${comId}` : '';
  const response = await instance.get(
    `${PREFIX_PATH}/statistics${queryParam}`
  ) as ApiResponse<Statistics>;
  
  if (response.success) {
    return response.data || {
      totalTools: 0,
      activeTools: 0,
      totalTasks: 0,
      activeTasks: 0,
      todayExecutions: 0,
      avgSuccessRate: 0
    };
  }
  
  return {
    totalTools: 0,
    activeTools: 0,
    totalTasks: 0,
    activeTasks: 0,
    todayExecutions: 0,
    avgSuccessRate: 0
  };
};

/**
 * Tool 상태 변경
 * @param toolId Tool ID
 * @param isActive 활성화 여부
 * @returns {Promise<boolean>}
 */
export const updateToolStatus = async (toolId: number, isActive: boolean): Promise<boolean> => {
  const response = await instance.post(
    `${PREFIX_PATH}/tools/${toolId}/status`,
    { isActive }
  ) as ApiResponse<any>;
  
  return response.success;
};

/**
 * Task 상태 변경
 * @param taskId Task ID
 * @param isActive 활성화 여부
 * @returns {Promise<boolean>}
 */
export const updateTaskStatus = async (taskId: number, isActive: boolean): Promise<boolean> => {
  const response = await instance.post(
    `${PREFIX_PATH}/tasks/${taskId}/status`,
    { isActive }
  ) as ApiResponse<any>;
  
  return response.success;
};

/**
 * Tool 도장별 사용여부 변경
 * @param toolId Tool ID
 * @param comId 도장 ID
 * @param isEnabled 사용 여부
 * @returns {Promise<boolean>}
 */
export const updateToolCompanyUsage = async (
  toolId: number, 
  comId: number, 
  isEnabled: boolean
): Promise<boolean> => {
  const response = await instance.post(
    `${PREFIX_PATH}/tools/${toolId}/company-usage`,
    { comId, isEnabled }
  ) as ApiResponse<any>;
  
  return response.success;
};

/**
 * Task 도장별 사용여부 변경
 * @param taskId Task ID
 * @param comId 도장 ID
 * @param isEnabled 사용 여부
 * @returns {Promise<boolean>}
 */
export const updateTaskCompanyUsage = async (
  taskId: number, 
  comId: number, 
  isEnabled: boolean
): Promise<boolean> => {
  const response = await instance.post(
    `${PREFIX_PATH}/tasks/${taskId}/company-usage`,
    { comId, isEnabled }
  ) as ApiResponse<any>;
  
  return response.success;
};

/**
 * Tool 상세 정보 조회
 * @param toolId Tool ID
 * @returns {Promise<ToolDetail>}
 */
export const getToolDetail = async (toolId: number): Promise<ToolDetail> => {
  const response = await instance.get(
    `${PREFIX_PATH}/tools/${toolId}/detail`
  ) as ApiResponse<ToolDetail>;
  return response.data;
};

/**
 * Task 상세 정보 조회
 * @param taskId Task ID
 * @returns {Promise<TaskDetail>}
 */
export const getTaskDetail = async (taskId: number): Promise<TaskDetail> => {
  const response = await instance.get(
    `${PREFIX_PATH}/tasks/${taskId}/detail`
  ) as ApiResponse<TaskDetail>;
  return response.data;
};

/**
 * 대시보드 데이터 일괄 조회
 * @param comId 도장 ID (선택적)
 * @returns {Promise<{tools: Tool[], tasks: Task[], statistics: Statistics}>}
 */
export const getDashboardData = async (comId?: number) => {
  try {
    const [toolsRes, tasksRes, statsRes] = await Promise.all([
      getTools(comId),
      getTasks(comId),
      getStatistics(comId)
    ]);
    
    return {
      tools: toolsRes,
      tasks: tasksRes,
      statistics: statsRes
    };
  } catch (error) {
    console.error('Dashboard data loading failed:', error);
    // 기본값 반환으로 UI 안정성 확보
    return {
      tools: [],
      tasks: [],
      statistics: {
        totalTools: 0,
        activeTools: 0,
        totalTasks: 0,
        activeTasks: 0,
        todayExecutions: 0,
        avgSuccessRate: 0
      }
    };
  }
};

/**
 * Tool 테스트 실행
 * @param toolId Tool ID
 * @param parameters 테스트 파라미터
 * @returns {Promise<any>}
 */
export const testTool = async (toolId: number, parameters: Record<string, any>): Promise<any> => {
  try {
    const response = await instance.post(
      `${PREFIX_PATH}/tools/${toolId}/test`,
      { parameters }
    ) as ApiResponse<any>;
    
    if (response.success) {
      return response.data;
    }
    throw new Error('Tool 테스트 실행에 실패했습니다.');
  } catch (error) {
    console.error('Tool test failed:', error);
    throw error;
  }
};

// === 기본 export ===
export const aiManagerApi = {
  // 기본 조회
  getCompanies,
  getTools,
  getTasks,
  getStatistics,
  getDashboardData,
  
  // 상세 조회
  getToolDetail,
  getTaskDetail,
  
  // 상태 변경
  updateToolStatus,
  updateTaskStatus,
  updateToolCompanyUsage,
  updateTaskCompanyUsage,
  
  // 기능 실행
  testTool,
};
